import * as React from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Thin animated top progress bar shown during route transitions.
 * Pulses on every pathname change for immediate user feedback.
 */
export function RouteProgress({ heightPx = 2 }: { heightPx?: number }) {
  const location = useLocation();
  const [active, setActive] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const timersRef = React.useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const firstRender = React.useRef(true);

  const clearTimers = React.useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);

  React.useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    clearTimers();
    setActive(true);
    setProgress(8);
    timersRef.current.push(setTimeout(() => setProgress(45), 60));
    timersRef.current.push(setTimeout(() => setProgress(80), 180));
    timersRef.current.push(
      setTimeout(() => {
        setProgress(100);
        timersRef.current.push(
          setTimeout(() => {
            setActive(false);
            setProgress(0);
          }, 180),
        );
      }, 320),
    );
    return () => clearTimers();
  }, [location.pathname, clearTimers]);

  React.useEffect(() => () => clearTimers(), [clearTimers]);

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: heightPx,
        zIndex: 9999,
        pointerEvents: 'none',
        opacity: active ? 1 : 0,
        transition: 'opacity 200ms ease-out',
      }}
    >
      <div
        style={{
          height: '100%',
          width: `${progress}%`,
          background: 'var(--tvl-accent, #6e1423)',
          boxShadow: '0 0 8px var(--tvl-accent, #6e1423)',
          transition: 'width 220ms ease-out',
        }}
      />
    </div>
  );
}

/** Centred spinner + label, intended as Suspense fallback for lazy-loaded routes. */
export function PageLoader({ label = 'Načítání…' }: { label?: string }) {
  return (
    <div className="flex h-full min-h-[60vh] w-full items-center justify-center bg-[var(--tvl-canvas)] text-[var(--tvl-ink-2)]">
      <div className="flex flex-col items-center gap-3">
        <span
          className="inline-block h-6 w-6 animate-spin rounded-full border-2 border-current border-t-transparent"
          role="status"
          aria-label={label}
        />
        <span className="text-xs">{label}</span>
      </div>
    </div>
  );
}
